import { useQuery } from "@tanstack/react-query";

import { listAlertRules } from "@/lib/api";
import { DEFAULT_BANDS, type SignalBands } from "@/lib/signal";
import type { AlertRule } from "@/lib/types";

/** Metrics that are an Rx power in dBm; both ends of the fibre share the bands. */
export const RX_METRICS = ["ont.rx_power_dbm", "ont.olt_rx_dbm"];

/**
 * The rule that owns one severity's Rx threshold: enabled, "<", on an Rx
 * metric. Panel colours and Telegram alerts must fire at the same number, so
 * there is one source of truth and it is the alert rule.
 */
export function rxRuleFor(rules: AlertRule[] | undefined, severity: "warn" | "critical"): AlertRule | undefined {
  return rules?.find(
    (r) => r.enabled && r.condition === "<" && r.severity === severity && RX_METRICS.includes(r.metric),
  );
}

export function bandsOf(rules: AlertRule[] | undefined): SignalBands {
  return {
    warn: rxRuleFor(rules, "warn")?.threshold ?? DEFAULT_BANDS.warn,
    crit: rxRuleFor(rules, "critical")?.threshold ?? DEFAULT_BANDS.crit,
  };
}

/** Colour bands for optical power. Falls back to the G.984 defaults while loading. */
export function useSignalBands(): SignalBands {
  const { data } = useQuery({ queryKey: ["alert-rules"], queryFn: listAlertRules, staleTime: 60_000 });
  return bandsOf(data?.rules);
}
